import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import { useNavigate } from 'react-router-dom'
import ThemeToggle from '../ui/ThemeToggle'
import NotificationsDropdown from '../notifications/NotificationsDropdown'
import { LogOut, Search, User as UserIcon } from 'lucide-react'
import { Menu } from '@headlessui/react'
import Avatar from '../ui/Avatar'

export default function Header({ onMenuToggle, sidebarCollapsed }) {
  const { user, logout } = useAuth()
  const { role } = usePermissions()
  const navigate = useNavigate()

  return (
    <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/95">
      <div className="flex items-center justify-between gap-3 px-3 py-3 sm:px-4 lg:px-6 xl:px-8">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 flex-shrink-0 active:scale-95"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            <svg className="h-6 w-6 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {!sidebarCollapsed && (
            <h1 className="hidden sm:block text-lg font-semibold text-gray-900 dark:text-white truncate animate-fade-in">
              Artemis
            </h1>
          )}
          <div className="hidden md:flex relative ml-1 max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Buscar..."
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/15 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 dark:focus:bg-gray-900"
            />
          </div>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3 flex-shrink-0">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notificaciones */}
          <NotificationsDropdown />

          {/* User Menu */}
          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3 pl-2 sm:pl-3 border-l border-gray-200 dark:border-gray-800">
            <div className="hidden md:block text-right">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[140px]">
                {user?.nombre} {user?.apellido || ''}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">
                {role?.replace('_', ' ').toLowerCase()}
              </p>
            </div>
            
            {/* User Dropdown Menu */}
            <Menu as="div" className="relative">
              <Menu.Button className="flex-shrink-0 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded-full">
                <Avatar user={user} size="md" />
              </Menu.Button>
              
              <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800 rounded-xl shadow-theme-lg ring-1 ring-gray-200 dark:ring-gray-800 focus:outline-none z-50">
                <div className="px-1 py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/perfil')}
                        className={`${
                          active ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300' : 'text-gray-700 dark:text-gray-200'
                        } group flex rounded-lg items-center w-full px-2 py-2 text-sm transition-colors`}
                      >
                        <UserIcon className="w-4 h-4 mr-3" />
                        Mi Perfil
                      </button>
                    )}
                  </Menu.Item>
                </div>
                <div className="px-1 py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={logout}
                        className={`${
                          active ? 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300' : 'text-red-600 dark:text-red-400'
                        } group flex rounded-lg items-center w-full px-2 py-2 text-sm transition-colors`}
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Cerrar Sesión
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  )
}
